import { isProjectAdminTab, PROJECT_ADMIN_SECTIONS } from '../../features/projects/admin/navigation';
import type { WorkspaceNavigationItem } from '../../types/workspaceNavigation';
import {
  BarChart3,
  Banknote,
  Bell,
  BookOpen,
  Briefcase,
  Building2,
  Bug,
  Calculator,
  CalculatorDevice,
  Bot,
  CalendarDays,
  CalendarClock,
  CalendarOff,
  Camera,
  CircleUser,
  Clock,
  ClipboardList,
  ClipboardCheck,
  CreditCard,
  Database,
  DocumentTakeoff,
  FileCheck,
  FileQuestion,
  FileSignature,
  FileText,
  Files,
  FileSpreadsheet,
  FolderOpen,
  Folder,
  Globe,
  HardHat,
  History,
  Home,
  Inbox,
  Landmark,
  LayoutDashboard,
  Plug,
  Library,
  LibraryBooks,
  Link as LinkIcon,
  ListTodo,
  Mail,
  MapPin,
  MessageSquare,
  NotebookPen,
  Phone,
  Percent,
  PoundSterling,
  Send,
  Star,
  Sparkles,
  ShoppingCart,
  Trash2,
  Truck,
  Package,
  Receipt,
  Ruler,
  Scale,
  Settings,
  Shield,
  ShieldCheck,
  SlidersHorizontal,
  StickyNote,
  UserCheck,
  Users,
  Wallet,
  Workflow,
  BookUser,
  KanbanSquare,
  type LucideIcon,
} from '@/shared/ui/icons';
import {SETTINGS_TABS} from '../../features/preferences/settingsTabs';
import { PREFERENCE_SECTIONS } from '../../features/preferences/preferencesSections';
import type { UserRole } from '../../types/auth';
import type { ApplicationWorkflow } from '../../contract-administration/types';
import { isInternalRole } from '../../utils/permissions';
import { canOpenSiteRegister } from '../../utils/siteRegisterAccess';
import { isContactsHubDestination, resolveContactsHubDestinations } from './contactsHub';

export type SidebarSectionId = 'main' | 'crm' | 'admin' | 'project' | 'settings' | 'templates' | 'clients' | 'profile' | 'preferences';

export type SidebarNavItem = {
  /** Authorised views grouped under this navigation entry. */
  destinations?: string[];
  id: string;
  label: string;
  // Headings are non-interactive group separators (Apple System Settings
  // / Mail accounts pattern). Renderers should skip the icon, render the
  // label as small all-caps muted text, and disable clicks.
  kind?: 'item' | 'heading';
  icon?: LucideIcon;
  sfSymbol?: string;
  section: SidebarSectionId;
  badge?: number;
  onClick?: () => void;
};

type SidebarNavContext = {
  role: UserRole | string;
  unreadMessageCount?: number;
  unreadEmailCount?: number;
  resolvedItems?: readonly WorkspaceNavigationItem[] | null;
  internalWorkspace?: boolean;
};

type RoleFlags = SidebarNavContext & {
  isSuperAdmin: boolean;
  isAdmin: boolean;
  isStaff: boolean;
  isClient: boolean;
  isSupervisor: boolean;
  // Narrower than isAdmin, which (per its own comment below) deliberately
  // also admits the customer-side `admin` account-holder role. Rows gated on
  // Contract Group's own staff, not a customer, must use this instead.
  isInternal: boolean;
};

type SidebarNavDefinition = {
  id: string;
  icon: LucideIcon;
  sfSymbol: string;
  section: SidebarSectionId;
  label: (flags: RoleFlags) => string;
  show: (flags: RoleFlags) => boolean;
  badge?: (flags: RoleFlags) => number | undefined;
};

export type ProjectSidebarTabContext = {
  isClient: boolean;
  isEstimatingPhase: boolean;
  activeTasksCount: number;
  activeClaimsCount: number;
  notesCount?: number;
  isStaff: boolean;
  projectId?: string;
  projectName?: string;
  projectStage?: string;
  internalUserIds?: string[];
  // Contract Group staff on an Employer-side project whose role profile runs
  // the tender (getContractRoleProfile(...).capabilities.runsTender).
  // Contractor-side projects never set this, so the tab stays hidden there.
  runsTender?: boolean;
  issuesContractDocuments?: boolean;
  applicationWorkflow?: ApplicationWorkflow;
  canGivePayLessNotice?: boolean;
  // The signed-in person's users.supervisor_role is a site role, so the
  // Site Register opens for them without canManageTeam (fire roll-call).
  isSiteSupervisor?: boolean;
};

export type ProjectSidebarPermissions = {
  canViewPricing?: boolean;
  canViewFinancial?: boolean;
  // Separate from canViewFinancial so workspace_owner/admin can see the
  // Applications tab read-only without also getting the Claims tab (which
  // remains gated by canViewFinancial).
  canViewApplications?: boolean;
  canViewItems?: boolean;
  canViewTasks?: boolean;
  canViewDocuments?: boolean;
  canViewPhotos?: boolean;
  canViewTeam?: boolean;
  canManageTeam?: boolean;
  canViewActivity?: boolean;
  readOnly?: boolean;
};

/** Payroll sections now live in the app sidebar on desktop. Headings are data
 * so web and touch drawers preserve the same functional grouping as the old
 * in-content rail. */
export function buildPayrollSidebarTabItems(): SidebarNavItem[] {
  return [
    { id: 'overview', label: 'Payroll overview', icon: LayoutDashboard, sfSymbol: 'square.grid.2x2.fill', section: 'main' },
    { id: '__h-employees', kind: 'heading', label: 'Employee management', section: 'main' },
    { id: 'employees', label: 'Employees', icon: Users, sfSymbol: 'person.2.fill', section: 'main' },
    { id: 'leave', label: 'Leave', icon: CalendarOff, sfSymbol: 'calendar.badge.minus', section: 'main' },
    { id: 'timesheets', label: 'Timesheets', icon: Clock, sfSymbol: 'clock.fill', section: 'main' },
    { id: '__h-processing', kind: 'heading', label: 'Payroll processing', section: 'main' },
    { id: 'pay-employees', label: 'Pay employees', icon: Banknote, sfSymbol: 'banknote.fill', section: 'main' },
    { id: 'pension-filings', label: 'Pension filings', icon: Shield, sfSymbol: 'shield.fill', section: 'main' },
    { id: 'rti-filings', label: 'RTI filings', icon: ClipboardCheck, sfSymbol: 'checkmark.seal.fill', section: 'main' },
    { id: 'taxes-and-filings', label: 'Taxes and filings', icon: FileText, sfSymbol: 'doc.text.fill', section: 'main' },
    { id: '__h-cis', kind: 'heading', label: 'Subcontractors (CIS)', section: 'main' },
    { id: 'subcontractors', label: 'Subcontractors', icon: Users, sfSymbol: 'person.3.fill', section: 'main' },
    { id: 'cis-returns', label: 'CIS returns', icon: HardHat, sfSymbol: 'hammer.fill', section: 'main' },
    { id: '__h-settings', kind: 'heading', label: 'Settings', section: 'main' },
    { id: 'settings', label: 'Payroll settings', icon: Settings, sfSymbol: 'gearshape.fill', section: 'main' },
  ];
}

export function buildPayrollProfileSidebarTabItems(): SidebarNavItem[] {
  return [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard, sfSymbol: 'square.grid.2x2.fill', section: 'main' },
    { id: 'pay', label: 'Pay', icon: Banknote, sfSymbol: 'banknote.fill', section: 'main' },
    { id: 'emergency', label: 'Emergency and notes', icon: Phone, sfSymbol: 'phone.fill', section: 'main' },
    { id: 'documents', label: 'Documents', icon: FileText, sfSymbol: 'doc.text.fill', section: 'main' },
    { id: 'timesheets', label: 'Timesheets', icon: Clock, sfSymbol: 'clock.fill', section: 'main' },
  ];
}

export function buildAccountingSidebarTabItems(): SidebarNavItem[] {
  return [
    { id: '__h-banking', kind: 'heading', label: 'Banking', section: 'main' },
    { id: 'bank-accounts', label: 'Bank accounts', icon: Landmark, sfSymbol: 'building.columns.fill', section: 'main' },
    { id: 'bank-rules', label: 'Bank rules', icon: SlidersHorizontal, sfSymbol: 'slider.horizontal.3', section: 'main' },
    { id: '__h-tools', kind: 'heading', label: 'Accounting tools', section: 'main' },
    { id: 'chart-of-accounts', label: 'Chart of accounts', icon: BarChart3, sfSymbol: 'chart.bar.fill', section: 'main' },
    { id: 'fixed-assets', label: 'Fixed assets', icon: Package, sfSymbol: 'archivebox.fill', section: 'main' },
    { id: '__h-settings', kind: 'heading', label: 'Settings', section: 'main' },
    { id: 'settings', label: 'Accounting settings', icon: Settings, sfSymbol: 'gearshape.fill', section: 'main' },
  ];
}

export function buildTaxSidebarTabItems(): SidebarNavItem[] {
  return [
    { id: 'vat-returns', label: 'VAT returns', icon: FileText, sfSymbol: 'doc.text.fill', section: 'main' },
    { id: 'cis-returns', label: 'CIS returns', icon: HardHat, sfSymbol: 'hammer.fill', section: 'main' },
    { id: '__h-settings', kind: 'heading', label: 'Settings', section: 'main' },
    { id: 'settings', label: 'Tax settings', icon: Settings, sfSymbol: 'gearshape.fill', section: 'main' },
  ];
}

// Order within a section is the array order below. Items are grouped by
// function: core daily-drivers (Home → Contacts) first, then the sales /
// finance block (Enquiries → Tax),
// then back-office admin. The three logical bands — Comms, Commercial, Admin —
// read top-to-bottom the way the team actually works a job.
const SIDEBAR_NAV_DEFINITIONS: SidebarNavDefinition[] = [
  // ── Core / daily drivers ──────────────────────────────────────────────────
  {
    id: 'dashboard',
    icon: Home,
    sfSymbol: 'house.fill',
    section: 'main',
    label: () => 'Home',
    show: ({ isStaff, isClient, isSupervisor }) => isStaff || isClient || isSupervisor,
  },
  {
    id: 'projects',
    icon: Building2,
    sfSymbol: 'building.2.fill',
    section: 'main',
    label: ({ isSupervisor }) => (isSupervisor ? 'My Projects' : 'Projects'),
    show: ({ isStaff, isClient, isSupervisor }) => isStaff || isClient || isSupervisor,
  },
  {
    id: 'calendar',
    icon: CalendarDays,
    sfSymbol: 'calendar',
    section: 'main',
    label: () => 'Calendar',
    show: ({ isStaff, isAdmin, isClient, isSupervisor }) => isStaff || isAdmin || isClient || isSupervisor,
  },
  {
    id: 'messages',
    icon: MessageSquare,
    sfSymbol: 'message.fill',
    section: 'main',
    label: () => 'Messages',
    show: ({ isStaff, isClient, isSupervisor }) => isStaff || isClient || isSupervisor,
    badge: ({ unreadMessageCount = 0 }) => (unreadMessageCount > 0 ? unreadMessageCount : undefined),
  },
  {
    // Sits next to Messages — both are communication, not back-office admin.
    // Group inboxes are folded into this view's own sidebar (see
    // buildEmailSidebarTabItems in AppSidebar.tsx); an `email` item whose id
    // starts with `group:` triggers the SharedInboxView.
    id: 'email',
    icon: Mail,
    sfSymbol: 'envelope.fill',
    section: 'main',
    label: () => 'Email',
    // Match the Gmail proxy and workspace renderer: mailbox access is
    // currently restricted to internal accounts.
    show: ({ isSuperAdmin, role }) => isSuperAdmin || role === 'internal',
    badge: ({ unreadEmailCount = 0 }) => (unreadEmailCount > 0 ? unreadEmailCount : undefined),
  },
  {
    id: 'contacts',
    icon: BookUser,
    sfSymbol: 'person.text.rectangle.fill',
    section: 'main',
    label: () => 'Contacts',
    show: ({ isStaff, isClient, isSupervisor }) => isStaff || isClient || isSupervisor,
  },
  {
    id: 'billing',
    icon: CreditCard,
    sfSymbol: 'creditcard.fill',
    section: 'main',
    label: () => 'Membership',
    show: ({ isClient }) => isClient,
  },
  // ── Commercial / finance ──────────────────────────────────────────────────
  {
    // Sits in front of Enquiries: this is the register of companies we are
    // still deciding about, Enquiries is the board of live opportunities.
    // `isInternal` is the whole of the Contract Group gating. The tables are
    // workspace-scoped, so opening this up later is a change to this line.
    // I3: was `isAdmin`, which also admits the customer-side `admin`
    // account-holder role — matches Task 12b's banned-phrase editor, which
    // already used isInternalRole for the same reason.
    id: 'outreach',
    icon: Send,
    sfSymbol: 'paperplane.fill',
    section: 'main',
    label: () => 'Outreach',
    show: ({ isInternal }) => isInternal,
  },
  {
    // Front of the sales funnel — leads straight into Sales.
    id: 'pipeline',
    icon: KanbanSquare,
    sfSymbol: 'chart.bar.doc.horizontal.fill',
    section: 'main',
    label: () => 'Enquiries',
    show: ({ isAdmin }) => isAdmin,
  },
  {
    id: 'workspace-pipeline',
    icon: KanbanSquare,
    sfSymbol: 'chart.bar.doc.horizontal.fill',
    section: 'main',
    label: () => 'Enquiries',
    show: ({ isClient }) => isClient,
  },
  {
    id: 'sales',
    icon: Banknote,
    sfSymbol: 'banknote.fill',
    section: 'main',
    label: () => 'Sales',
    show: ({ isStaff, role }) => isStaff || role === 'workspace_owner' || role === 'workspace_admin',
  },
  {
    id: 'purchases',
    icon: ShoppingCart,
    sfSymbol: 'cart.fill',
    section: 'main',
    label: () => 'Purchases',
    show: ({ isStaff, role }) => isStaff || role === 'workspace_owner' || role === 'workspace_admin',
  },
  {
    id: 'payroll',
    icon: Wallet,
    sfSymbol: 'banknote.fill',
    section: 'main',
    label: () => 'Payroll',
    show: ({ isStaff, role }) => isStaff || role === 'workspace_owner' || role === 'workspace_admin',
  },
  {
    id: 'accounting',
    icon: BookOpen,
    sfSymbol: 'book.closed.fill',
    section: 'main',
    label: () => 'Accounting',
    show: ({ isStaff, role }) => isStaff || role === 'workspace_owner' || role === 'workspace_admin',
  },
  {
    id: 'tax',
    icon: Percent,
    sfSymbol: 'percent',
    section: 'main',
    label: () => 'Tax',
    show: ({ isStaff, role }) => isStaff || role === 'workspace_owner' || role === 'workspace_admin',
  },
  // ── Back-office / admin ───────────────────────────────────────────────────
  {
    id: 'workspaces',
    icon: Building2,
    sfSymbol: 'building.2.fill',
    section: 'admin',
    label: () => 'Workspaces',
    // Cross-workspace administration is a Quantis-team surface. Canonical
    // super_admin and internal users need it (including the internal-only
    // Subscription editor); customer workspace roles must never see it.
    show: ({ isSuperAdmin, role }) => isSuperAdmin || role === 'internal',
  },
  // Billing is no longer its own admin screen — it is the Subscriptions and
  // Platform Revenue sections of the Sales hub. /billing still forwards there
  // for old links (see AuthenticatedController), and the finance-billing view
  // itself is retained so those forwards and any deep link keep resolving.
  {
    id: 'employers',
    icon: Briefcase,
    sfSymbol: 'briefcase.fill',
    section: 'admin',
    label: () => 'Employers',
    show: ({ isStaff }) => isStaff,
  },
  {
    id: 'resource-library',
    icon: LibraryBooks,
    sfSymbol: 'books.vertical.fill',
    section: 'admin',
    label: () => 'Resource Library',
    // Resource libraries are workspace-scoped for contractors. Keep the
    // shared catalogue and workspace overrides available to workspace users
    // without exposing the staff-only library management actions (those are
    // still gated by ResourceLibraryManagement.canManage).
    show: ({ isStaff, isClient }) => isStaff || isClient,
  },
  // Plant Register moved off the main nav — it now lives inside a project's
  // Health & Safety hub, beside RAMS/Toolbox Talks/Plant Checks (compliance,
  // not an asset list: most plant is hired, not owned). /plant is still a
  // working address (deep links, the Apple app), just not a sidebar row.
  // Settings (formerly "System Settings") is reached from the profile popout
  // menu, not the main nav — see the profile actions in AppSidebar.tsx.
];

export function getRoleFlags(context: SidebarNavContext): RoleFlags {
  // Unknown roles fail closed as a workspace member. Recognise both vocabularies so the
  // sidebar populates correctly whether the user record is fresh
  // (super_admin / internal / member) or a stale cached session
  // still carrying admin / staff / client / supervisor.
  const role = context.role ?? 'member';
  const isSuperAdmin = role === 'super_admin';
  const isAdmin = role === 'super_admin' || role === 'internal'
               || role === 'admin'       || role === 'staff';
  const isStaff = role === 'internal' || role === 'staff' || isAdmin;
  const isClient =
    role === 'workspace_owner' || role === 'workspace_admin' || role === 'workspace_user' ||
    role === 'member'          || role === 'client';
  const isSupervisor = role === 'workspace_user' || role === 'supervisor';
  const isInternal = isInternalRole(role as UserRole);

  return {
    ...context,
    isSuperAdmin,
    isAdmin,
    isStaff,
    isClient,
    isSupervisor,
    isInternal,
  };
}

export function mainNavigationDestination(view: string): string | null {
  const aliases: Record<string, string> = { 'project-detail': 'projects', 'add-project': 'projects', 'user-form': 'workspaces', 'client-detail': 'workspaces', 'add-workspace': 'workspaces', 'request-client': 'workspaces' };
  // Employers and Workspaces keep their grants even though the rail groups them under Contacts.
  const id = aliases[view] ?? view;
  return SIDEBAR_NAV_DEFINITIONS.some(item => item.id === id) ? id : null;
}

export function resolveNavigationPresentation(grants: readonly WorkspaceNavigationItem[], definitions: readonly SidebarNavItem[]): SidebarNavItem[] {
  const byId = new Map(definitions.map(item => [item.id, item]));
  const icons: Record<string, LucideIcon> = { BarChart3, Banknote, Bell, BookOpen, Briefcase, Building2, Bug, Calculator, Bot, CalendarDays, CalendarClock, CalendarOff, Camera, CircleUser, Clock, ClipboardList, ClipboardCheck, CreditCard, Database, FileCheck, FileQuestion, FileSignature, FileText, Files, FileSpreadsheet, FolderOpen, Folder, Globe, HardHat, History, Home, Inbox, Landmark, LayoutDashboard, Plug, Library, LibraryBooks, LinkIcon, ListTodo, Mail, MapPin, MessageSquare, NotebookPen, Phone, Percent, Send, Star, ShoppingCart, Trash2, Truck, Package, Receipt, Ruler, Scale, Settings, Shield, ShieldCheck, SlidersHorizontal, StickyNote, UserCheck, Users, Wallet, Workflow, BookUser, KanbanSquare };
  return [...grants].sort((a, b) => a.sortOrder - b.sortOrder).flatMap(grant => {
    const definition = byId.get(grant.id);
    if (!definition) return [];
    return [{ ...definition, label: grant.label, icon: icons[grant.iconKey] ?? definition.icon, sfSymbol: grant.sfSymbol || definition.sfSymbol, section: grant.section as SidebarSectionId }];
  });
}

export const PIPELINE_DESTINATIONS = ['pipeline', 'workspace-pipeline', 'projects'] as const;
export type PipelineDestination = (typeof PIPELINE_DESTINATIONS)[number];

export function isPipelineDestination(value: string): value is PipelineDestination {
  return (PIPELINE_DESTINATIONS as readonly string[]).includes(value);
}

/** One visible destination; the underlying grant IDs stay distinct for access checks. */
export function consolidatePipelineNavigation(items: readonly SidebarNavItem[]): SidebarNavItem[] {
  const destination = (['projects', 'pipeline', 'workspace-pipeline'] as PipelineDestination[])
    .map(id => items.find(item => item.id === id))
    .find(item => item !== undefined);
  if (!destination) return [...items];
  const remaining = items.filter(item => !isPipelineDestination(item.id));
  const projectsIndex = items.findIndex(item => item.id === 'projects');
  const originalIndex = projectsIndex >= 0 ? projectsIndex : items.findIndex(item => isPipelineDestination(item.id));
  const insertionIndex = items.slice(0, originalIndex).filter(item => !isPipelineDestination(item.id)).length;
  remaining.splice(insertionIndex, 0, {
    ...destination, label: 'Pipeline', icon: KanbanSquare, sfSymbol: 'chart.bar.doc.horizontal.fill',
  });
  return remaining;
}

export const ACCOUNTING_DESTINATIONS = ['accounting', 'sales', 'purchases', 'payroll', 'tax'] as const;
export type AccountingDestination = (typeof ACCOUNTING_DESTINATIONS)[number];

export function isAccountingDestination(value: string): value is AccountingDestination {
  return (ACCOUNTING_DESTINATIONS as readonly string[]).includes(value);
}

export function accountingAreaItems(destinations: readonly AccountingDestination[]) {
  return ACCOUNTING_DESTINATIONS.filter(id => destinations.includes(id)).map(id => {
    const definition = SIDEBAR_NAV_DEFINITIONS.find(item => item.id === id)!;
    return { id, label: id.charAt(0).toUpperCase() + id.slice(1), icon: definition.icon };
  });
}

/** Group presentation only; the backing ID must be one of the user's raw grants. */
export function consolidateAccountingNavigation(items: readonly SidebarNavItem[]): SidebarNavItem[] {
  const destination = ACCOUNTING_DESTINATIONS.map(id => items.find(item => item.id === id)).find(Boolean);
  if (!destination) return [...items];
  const index = items.findIndex(item => isAccountingDestination(item.id));
  const remaining = items.filter(item => !isAccountingDestination(item.id));
  remaining.splice(index, 0, { ...destination, label: 'Accounting', icon: BookOpen, sfSymbol: 'book.closed.fill' });
  return remaining;
}

/** QuantisAI is a shell tool like its header entry, not a workspace feature grant. */
export function withQuantisAINavigation(items: readonly SidebarNavItem[]): SidebarNavItem[] {
  const remaining = items.filter(item => item.id !== 'quantis-ai');
  const home = remaining.findIndex(item => item.id === 'dashboard');
  remaining.splice(home + 1, 0, { id: 'quantis-ai', label: 'QuantisAI', icon: Sparkles, sfSymbol: 'sparkles', section: 'main' });
  return remaining;
}

export function getSidebarNavGroups(context: SidebarNavContext) {
  const flags = getRoleFlags(context);

  const items = context.resolvedItems !== undefined
    ? resolveNavigationPresentation(context.resolvedItems ?? [], SIDEBAR_NAV_DEFINITIONS.map(definition => ({ id: definition.id, label: definition.label(flags), icon: definition.icon, sfSymbol: definition.sfSymbol, section: definition.section, badge: definition.badge?.(flags) })))
    : SIDEBAR_NAV_DEFINITIONS
    .filter((definition) => definition.show(flags))
    .map((definition) => ({
      id: definition.id,
      label: definition.label(flags),
      icon: definition.icon,
      sfSymbol: definition.sfSymbol,
      section: definition.section as SidebarSectionId,
      badge: definition.badge?.(flags),
      onClick: undefined as (() => void) | undefined,
    }));

  // Contacts, Employers and Workspaces share one entry that always opens the Companies page. Whichever is granted backs it.
  const contactsHubDestinations = resolveContactsHubDestinations(
    items.filter(item => isContactsHubDestination(item.id)).map(item => item.id), flags);
  const hubBacking = items.find(item => item.id === contactsHubDestinations[0]);
  const hubIndex = items.findIndex(item => item.id === 'contacts');
  const railItems = items.filter(item => !isContactsHubDestination(item.id));
  if (hubBacking) {
    railItems.splice(hubIndex >= 0 ? items.slice(0, hubIndex).filter(item => !isContactsHubDestination(item.id)).length : railItems.length, 0,
      { ...hubBacking, id: 'contacts', label: 'Contacts', icon: BookUser, sfSymbol: 'person.text.rectangle.fill', section: 'main' as SidebarSectionId });
  }

  // Intersect related grants with their existing mounted-view role gates.
  const pipelineDestinations = railItems.filter(item => item.section === 'main' && isPipelineDestination(item.id))
    .filter(item => item.id !== 'pipeline' || flags.isAdmin)
    .map(item => item.id as PipelineDestination);
  const accountingDestinations = railItems.filter(item => item.section === 'main' && isAccountingDestination(item.id))
    .filter(() => flags.isStaff || flags.role === 'workspace_owner' || flags.role === 'workspace_admin')
    .map(item => item.id as AccountingDestination);
  const mainItems = railItems.filter(item => item.section === 'main' &&
    (item.id !== 'outreach' || (flags.isInternal && context.internalWorkspace === true)) &&
    (!isPipelineDestination(item.id) || pipelineDestinations.includes(item.id)) &&
    (!isAccountingDestination(item.id) || accountingDestinations.includes(item.id)));

  return {
    pipelineDestinations, accountingDestinations, contactsHubDestinations,
    // Consolidation folds Enquiries into the Pipeline row, so menus read the raw ids.
    menuGrants: mainItems.map(item => item.id),
    mainNavItems: consolidateAccountingNavigation(consolidatePipelineNavigation(mainItems)),
    crmNavItems: railItems.filter((item) => item.section === 'crm'),
    adminNavItems: railItems.filter((item) => item.section === 'admin'),
  };
}

export function buildNativeSidebarNavItems(context: SidebarNavContext) {
  const { mainNavItems, crmNavItems, adminNavItems } = getSidebarNavGroups(context);

  return [...mainNavItems, ...crmNavItems, ...adminNavItems].map(({ id, label, sfSymbol, section }) => ({
    id,
    label,
    sfSymbol,
    section,
  }));
}

export function buildProjectSidebarTabItems(
  projectTabProps?: ProjectSidebarTabContext | null,
  projectPermissions?: ProjectSidebarPermissions | null,
  resolvedItems?: readonly WorkspaceNavigationItem[] | null,
  activeTab?: string | null,
): SidebarNavItem[] {
  let items = buildProjectSidebarTabDefinitions(projectTabProps, projectPermissions, resolvedItems)
    .map(item => item.id === 'interim' ? {
      ...item,
      label: item.label === 'Applications' ? 'Valuations' : item.label,
      icon: PoundSterling,
      sfSymbol: 'sterlingsign',
    } : item.id === 'pricing' ? { ...item, icon: CalculatorDevice, sfSymbol: 'calculator' }
      : item.id === 'takeoff' ? { ...item, icon: DocumentTakeoff, sfSymbol: 'square.on.square' } : item);
  const notebookItems = items.filter(item => item.id === 'tasks' || item.id === 'notes');
  const notebookDestination = notebookItems.find(item => item.id === activeTab)
    ?? notebookItems.find(item => item.id === 'notes') ?? notebookItems[0];
  if (notebookDestination) {
    const index = items.findIndex(item => item.id === 'tasks' || item.id === 'notes');
    items = items.filter(item => item.id !== 'tasks' && item.id !== 'notes');
    // Preserve the authorised route ID: grouping never grants access to the other section.
    items.splice(index, 0, { ...notebookDestination, label: 'Notebook', icon: BookOpen, sfSymbol: 'book.closed.fill' });
  }
  const changesItems = items.filter(item => item.id === 'events' || item.id === 'claims' || item.id === 'rfis');
  const changesDestination = changesItems.find(item => item.id === activeTab)
    ?? changesItems.find(item => item.id === 'events') ?? changesItems[0];
  if (changesDestination) {
    const existingIndex = items.findIndex(item => item.id === 'events' || item.id === 'claims');
    const anchor = existingIndex >= 0 ? existingIndex : items.findIndex(item => item.id === 'rfis');
    const index = items.slice(0, anchor).filter(item => !changesItems.includes(item)).length;
    const claimsBadge = changesItems.find(item => item.id === 'claims')?.badge;
    items = items.filter(item => item.id !== 'events' && item.id !== 'claims' && item.id !== 'rfis');
    items.splice(index, 0, { ...changesDestination, label: 'Changes', icon: Workflow, sfSymbol: 'arrow.triangle.branch', badge: claimsBadge, destinations: changesItems.map(item => item.id) });
  }
  const activity = items.find(item => item.id === 'activity');
  if (activity) {
    items = items.filter(item => item.id !== 'activity');
    if (!items.some(item => item.id === 'admin')) {
      // The Admin group is also reachable for users granted only Activity.
      items.push({ ...activity, label: 'Admin', icon: Settings, sfSymbol: 'gearshape.fill' });
    }
  }
  // Admin is one canvas whose sections deep-link as their own tabs; the rail
  // entry follows whichever of them is open so it stays highlighted.
  if (isProjectAdminTab(activeTab)) {
    const adminIndex = items.findIndex(item => item.id === 'admin' || item.id === 'admin/contract' || item.id === 'activity');
    if (adminIndex >= 0) items = items.map((item, index) => index === adminIndex ? { ...item, id: activeTab! } : item);
  }
  return items;
}

export function buildProjectAdminSidebarTabItems(
  projectTabProps?: ProjectSidebarTabContext | null,
  projectPermissions?: ProjectSidebarPermissions | null,
  resolvedItems?: readonly WorkspaceNavigationItem[] | null,
): SidebarNavItem[] {
  const authorised = buildProjectSidebarTabDefinitions(projectTabProps, projectPermissions, resolvedItems);
  const ids = new Set(authorised.map(item => item.id));
  const icons = { project: Building2, appointment: Users, contract: FileText, payment: Banknote, dates: CalendarDays, activity: History };
  return PROJECT_ADMIN_SECTIONS.filter(item => item.section === 'activity'
    ? ids.has('activity')
    : item.section === 'contract' || item.section === 'payment'
      ? ids.has('admin') || ids.has('admin/contract')
      : ids.has('admin'))
    .map(item => ({ id: item.id, label: item.label, sfSymbol: item.sfSymbol, icon: icons[item.section], section: 'project' }));
}

function buildProjectSidebarTabDefinitions(
  projectTabProps?: ProjectSidebarTabContext | null,
  projectPermissions?: ProjectSidebarPermissions | null,
  resolvedItems?: readonly WorkspaceNavigationItem[] | null
): SidebarNavItem[] {
  if (!projectTabProps) return [];
  if (resolvedItems !== undefined) {
    const definitions = buildProjectSidebarTabDefinitions({ ...projectTabProps, isClient: false, isStaff: true, isSiteSupervisor: true }, {});
    definitions.push({ id: 'admin/contract', label: 'Financials', icon: Settings, sfSymbol: 'sterlingsign.circle', section: 'project' });
    const items = resolveNavigationPresentation(resolvedItems ?? [], definitions);
    return items.some(item => item.id === 'admin') ? items.filter(item => item.id !== 'admin/contract') : items;
  }
  // The live app passes null until the server has confirmed this project's
  // grants. Show only the always-available overview during that interval;
  // an old project's permissions must never drive the new project's rail.
  if (projectPermissions === null) {
    return [{
      id: 'overview', label: 'Dashboard', icon: LayoutDashboard,
      sfSymbol: 'square.grid.2x2.fill', section: 'project',
    }];
  }

  const canViewPricing = projectPermissions?.canViewPricing !== false;
  const canViewFinancial = projectPermissions?.canViewFinancial !== false;
  // Falls back to canViewFinancial for KV-stored permission records that pre-date
  // this field so existing grants are not silently downgraded.
  const canViewApplications =
    projectPermissions?.canViewApplications !== undefined
      ? projectPermissions.canViewApplications
      : canViewFinancial;
  const canViewTasks = projectPermissions?.canViewTasks !== false;
  const canViewDocuments = projectPermissions?.canViewDocuments !== false;
  const canViewPhotos = projectPermissions?.canViewPhotos !== false;
  const canViewItems = projectPermissions?.canViewItems !== false;
  const canViewTeam = projectPermissions?.canViewTeam !== false;
  const canViewActivity = projectPermissions?.canViewActivity !== false;

  return [
    {
      id: 'overview',
      label: 'Dashboard',
      icon: LayoutDashboard,
      sfSymbol: 'square.grid.2x2.fill',
      section: 'project',
    },
    // Grouped into workflow sections so the project nav scans as four short
    // lists instead of one 16-row wall. Heading rows (kind: 'heading') render
    // as muted group labels on web (AppSidebar) and are folded into native
    // section groupings by buildNativeNavItems — they never become tappable
    // rows. Dashboard stays ungrouped at the top as the landing tab.
    { id: '__h-commercial', kind: 'heading', label: 'Commercial', section: 'project' },
    {
      // Takeoff feeds the priced BOQ (Pricing), which feeds Applications:
      // keep the money flow (measure -> price -> bill) reading top to bottom.
      id: 'takeoff',
      label: 'Takeoff',
      icon: DocumentTakeoff,
      sfSymbol: 'square.on.square',
      section: 'project',
    },
    {
      id: 'pricing',
      label: 'Pricing',
      icon: CalculatorDevice,
      sfSymbol: 'calculator',
      section: 'project',
    },
    {
      // Employer-side only: bidders, the ITT, the comparison and the award.
      // Hidden by default (see the `runsTender` filter case below).
      id: 'tendering',
      label: 'Tendering',
      icon: FileSpreadsheet,
      sfSymbol: 'tablecells.fill',
      section: 'project',
    },
    {
      id: 'interim',
      label: 'Valuations',
      icon: PoundSterling,
      sfSymbol: 'sterlingsign',
      section: 'project',
    },
    {
      // Employer-side only: the register of certificates, notices and
      // instructions issued under the Contract. Hidden by default (see the
      // `issuesContractDocuments` filter case below).
      id: 'contract-admin',
      label: 'Notices',
      icon: FileCheck,
      sfSymbol: 'checkmark.seal.fill',
      section: 'project',
    },
    {
      id: 'claims',
      label: 'Claims',
      icon: Scale,
      sfSymbol: 'exclamationmark.triangle.fill',
      section: 'project',
      badge: projectTabProps.activeClaimsCount > 0 ? projectTabProps.activeClaimsCount : undefined,
    },
    {
      id: 'procurement',
      label: 'Procurement',
      icon: Truck,
      sfSymbol: 'truck.box.fill',
      section: 'project',
    },
    {
      // Events records the commercial events that can lead to instructions,
      // dayworks and claims, so it belongs with the project's commercial flow.
      id: 'events',
      label: 'Events',
      icon: NotebookPen,
      sfSymbol: 'square.and.pencil',
      section: 'project',
    },
    { id: '__h-delivery', kind: 'heading', label: 'Operations', section: 'project' },
    {
      id: 'tasks',
      label: 'Tasks',
      icon: ListTodo,
      sfSymbol: 'checklist',
      section: 'project',
      badge: projectTabProps.activeTasksCount > 0 ? projectTabProps.activeTasksCount : undefined,
    },
    {
      id: 'planner',
      label: 'Planner',
      icon: CalendarClock,
      sfSymbol: 'calendar',
      section: 'project',
    },
    {
      id: 'site_photos',
      label: 'Site Photos',
      icon: Camera,
      sfSymbol: 'camera.fill',
      section: 'project',
    },
    {
      // Who signed in and out of site with Quantis Field. Scans carry each
      // person's hours and location, so only team managers and site
      // supervisors see it (utils/siteRegisterAccess, mirroring the API).
      id: 'site-register',
      label: 'Site Register',
      icon: UserCheck,
      sfSymbol: 'person.crop.circle.fill.badge.checkmark',
      section: 'project',
    },
    {
      id: 'hs',
      label: 'H&S',
      icon: HardHat,
      sfSymbol: 'cross.case.fill',
      section: 'project',
    },
    {
      id: 'snagging',
      label: 'Snagging',
      icon: ClipboardCheck,
      sfSymbol: 'checklist',
      section: 'project',
    },
    {
      id: 'reports',
      label: 'Reports',
      icon: BarChart3,
      sfSymbol: 'chart.bar.fill',
      section: 'project',
    },
    {
      id: 'om-manual',
      label: 'O&M Manual',
      icon: BookOpen,
      sfSymbol: 'book.closed.fill',
      section: 'project',
    },
    { id: '__h-records', kind: 'heading', label: 'Records', section: 'project' },
    {
      id: 'documents',
      label: 'Documents',
      icon: Folder,
      sfSymbol: 'folder.fill',
      section: 'project',
    },
    {
      id: 'email',
      label: 'Email',
      icon: Mail,
      sfSymbol: 'envelope.fill',
      section: 'project',
    },
    {
      id: 'notes',
      label: 'Notes',
      icon: StickyNote,
      sfSymbol: 'note.text',
      section: 'project',
      badge: projectTabProps.notesCount ?? undefined,
    },
    {
      id: 'activity',
      label: 'Activity',
      icon: History,
      sfSymbol: 'clock.arrow.circlepath',
      section: 'project',
    },
    {
      id: 'rfis',
      label: 'RFI Register',
      icon: FileQuestion,
      sfSymbol: 'questionmark.folder.fill',
      section: 'project',
    },
    { id: '__h-manage', kind: 'heading', label: 'Manage', section: 'project' },
    {
      id: 'team',
      label: 'Team',
      icon: Users,
      sfSymbol: 'person.2.fill',
      section: 'project',
    },
    {
      id: 'admin',
      label: 'Admin',
      icon: Settings,
      sfSymbol: 'gearshape.fill',
      section: 'project',
    },
    {
      id: 'admin/contract',
      label: 'Financials',
      icon: Settings,
      sfSymbol: 'sterlingsign.circle',
      section: 'project',
    },
  ].filter((tab) => {
    switch (tab.id) {
      case 'pricing':
        return canViewPricing;
      case 'tendering':
        return canViewPricing && projectTabProps.runsTender === true;
      case 'contract-admin':
        return canViewFinancial && projectTabProps.issuesContractDocuments === true;
      case 'interim':
        return !projectTabProps.isEstimatingPhase
          && canViewApplications
          && projectTabProps.applicationWorkflow !== 'view';
      case 'procurement':
        return canViewPricing;
      case 'claims':
        return !projectTabProps.isEstimatingPhase && canViewFinancial;
      case 'tasks':
        return !projectTabProps.isClient && canViewTasks;
      case 'planner':
        return canViewTasks && canViewFinancial;
      case 'site_photos':
        return canViewPhotos;
      case 'site-register':
        return canOpenSiteRegister(projectPermissions, projectTabProps.isSiteSupervisor === true);
      case 'documents':
        return canViewDocuments;
      case 'email':
        return !projectTabProps.isClient && canViewActivity;
      case 'team':
        return canViewTeam;
      case 'activity':
        return !projectTabProps.isClient && canViewActivity;
      case 'notes':
        return !projectTabProps.isClient;
      case 'snagging':
        return !projectTabProps.isClient && canViewTasks;
      case 'rfis':
        return canViewDocuments;
      case 'takeoff':
        return !projectTabProps.isClient && canViewItems;
      case 'admin':
        return projectTabProps.isStaff || projectPermissions?.canManageTeam === true;
      case 'admin/contract':
        return projectPermissions?.canViewFinancial === true &&
          !projectTabProps.isStaff && projectPermissions?.canManageTeam !== true;
      default:
        return true;
    }
  }) as SidebarNavItem[];
}

export function buildEmailSidebarTabItems(unreadByLabel?: Record<string, number>): SidebarNavItem[] {
  const u = (id: string) => unreadByLabel?.[id];
  return [
    { id: 'INBOX',   label: 'Inbox',   icon: Inbox,       section: 'main', sfSymbol: 'tray.full.fill',     badge: u('INBOX') },
    { id: 'STARRED', label: 'Starred', icon: Star,        section: 'main', sfSymbol: 'star.fill',          badge: u('STARRED') },
    { id: 'SENT',    label: 'Sent',    icon: Send,        section: 'main', sfSymbol: 'paperplane.fill',    badge: u('SENT') },
    { id: 'DRAFT',   label: 'Drafts',  icon: FileText,    section: 'main', sfSymbol: 'doc.text.fill',      badge: u('DRAFT') },
    { id: 'SPAM',    label: 'Spam',    icon: ShieldCheck, section: 'main', sfSymbol: 'exclamationmark.octagon.fill', badge: u('SPAM') },
    { id: 'TRASH',   label: 'Trash',   icon: Trash2,      section: 'main', sfSymbol: 'trash.fill',         badge: u('TRASH') },
  ];
}

/** The personal Preferences sections, in the order the pane and the phone list show them. */
export function buildPreferencesSidebarTabItems(): SidebarNavItem[] {
  return PREFERENCE_SECTIONS.map(({ id, label, icon, sfSymbol }) => ({ id, label, icon, section: 'preferences', sfSymbol }));
}

export function buildSystemSettingsSidebarTabItems({includeOutreachTemplates = false,role}: {includeOutreachTemplates?: boolean;role?:string} = {}): SidebarNavItem[] {
  const items: SidebarNavItem[] = [
    { id: '__h-connections', kind: 'heading', label: 'Connections', section: 'settings' },
    { id: 'integrations', label: 'System plugins', icon: Plug,    section: 'settings', sfSymbol: 'powerplug' },
    { id: '__h-automation', kind: 'heading', label: 'Automation', section: 'settings' },
    { id: 'workflows',    label: 'Workflows',    icon: Workflow, section: 'settings', sfSymbol: 'point.3.connected.trianglepath.dotted' },
    { id: 'workflows/trigger-catalog', label: 'Trigger Catalog', icon: BookOpen, section: 'settings', sfSymbol: 'book.closed.fill' },
    { id: '__h-templates', kind: 'heading', label: 'Templates', section: 'templates' },
    { id: 'templates',               label: 'Export Layouts', icon: FileText, section: 'templates', sfSymbol: 'doc.richtext.fill' },
    { id: 'templates/email',         label: 'Email templates',          icon: Mail,     section: 'templates', sfSymbol: 'envelope.fill' },
    { id: 'templates/notifications', label: 'Notification templates',  icon: Bell,     section: 'templates', sfSymbol: 'bell.badge.fill' },
    { id: 'templates/documents',     label: 'Document templates',      icon: Files,    section: 'templates', sfSymbol: 'doc.on.doc.fill' },
    ...(includeOutreachTemplates ? [{id:'templates/outreach', label:'Outreach', icon:Send, section:'templates' as SidebarSectionId, sfSymbol:'paperplane.fill'}] : []),
    { id: 'boq-formatting',  label: 'BOQ Formatting',  icon: FileText, section: 'settings', sfSymbol: 'list.bullet.indent' },
    { id: '__h-administration', kind: 'heading', label: 'Administration', section: 'settings' },
    { id: 'rfis',            label: 'RFI Register',    icon: FileQuestion, section: 'settings', sfSymbol: 'questionmark.folder.fill' },
    { id: 'audit',         label: 'Audit Trail',   icon: History,  section: 'settings', sfSymbol: 'clock.arrow.circlepath' },
    { id: 'support-tickets', label: 'Support Tickets', icon: Bug,      section: 'settings', sfSymbol: 'ladybug.fill' },
  ];
  return items.filter(item => {
    if (!role || item.kind === 'heading') return true;
    const admin = role === 'admin' || role === 'super_admin';
    if (item.id === 'workflows') return admin || isInternalRole(role as UserRole);
    if (['rfis','support-tickets'].includes(item.id)) return admin;
    if (['workflows/trigger-catalog','templates/email','templates/notifications'].includes(item.id)) return isInternalRole(role as UserRole);
    return true;
  });
}

export const WORKSPACE_PROFILE_TAB_PREFIX = 'workspace/';

export const SELF_SERVICE_WORKSPACE_TAB_IDS = [
  'integrations',
  'overview',
  'trade-profile',
  'stripe-invoices',
  'users',
  'bots',
  'documents',
] as const;

export type SelfServiceWorkspaceTabId = typeof SELF_SERVICE_WORKSPACE_TAB_IDS[number];

export function isSelfServiceWorkspaceTab(value: string): value is SelfServiceWorkspaceTabId {
  return (SELF_SERVICE_WORKSPACE_TAB_IDS as readonly string[]).includes(value);
}

const WORKSPACE_USER_ROUTE = /^users\/([a-z0-9]+(?:-[a-z0-9]+)*)$/;
const UUID_ROUTE_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const RESERVED_WORKSPACE_USER_SLUGS = new Set(['invite', 'new', 'add', 'edit', 'remove', 'delete']);

/** Returns the readable member slug carried by `/profile/workspace/users/:slug`.
 * Keeping this parser beside the access allow-list means a new nested Users
 * route cannot accidentally bypass the workspace-settings route guard. */
export function getSelfServiceWorkspaceUserSlug(value: string): string | null {
  const slug = value.match(WORKSPACE_USER_ROUTE)?.[1] ?? null;
  return slug
    && !UUID_ROUTE_SEGMENT.test(slug)
    && !RESERVED_WORKSPACE_USER_SLUGS.has(slug)
    ? slug
    : null;
}

const WORKSPACE_BOT_ROUTE = /^bots\/([a-z0-9]+(?:-[a-z0-9]+)*)$/;

/** Readable bot slug carried by `/profile/workspace/bots/:slug`; `new` opens
 * the create form. Same shape rules as the users parser above. */
export function getSelfServiceWorkspaceBotSlug(value: string): string | null {
  const slug = value.match(WORKSPACE_BOT_ROUTE)?.[1] ?? null;
  return slug && !UUID_ROUTE_SEGMENT.test(slug) ? slug : null;
}

const WORKSPACE_DOCUMENT_ROUTE = /^documents(?:\/[^/]+)+$/;

export function isSelfServiceWorkspaceRoute(value: string): boolean {
  return isSelfServiceWorkspaceTab(value)
    || WORKSPACE_DOCUMENT_ROUTE.test(value)
    || ['integrations/xero', 'integrations/google-workspace', 'integrations/gmail-addon', 'integrations/push-notifications'].includes(value)
    || value === 'users/invite'
    || getSelfServiceWorkspaceUserSlug(value) !== null
    || getSelfServiceWorkspaceBotSlug(value) !== null;
}

export function buildProfileSidebarTabItems(
  options: { includeWorkspaceSettings?: boolean; includeAgents?: boolean; includePlugins?: boolean } = {},
): SidebarNavItem[] {
  const personalItems: SidebarNavItem[] = [
    { id: '__h-account', kind: 'heading', label: 'Account', section: 'profile' },
    { id: 'overview',      label: 'Account',          icon: CircleUser,    section: 'profile', sfSymbol: 'person.crop.circle' },
    { id: '__h-availability', kind: 'heading', label: 'Availability', section: 'profile' },
    { id: 'availability', label: 'Availability', icon: Clock, section: 'profile', sfSymbol: 'clock' },
    { id: '__h-personal-templates', kind: 'heading', label: 'Templates', section: 'profile' },
    { id: 'signature', label: 'Email signatures', icon: Mail, section: 'profile', sfSymbol: 'signature' },
    { id: '__h-personal-connections', kind: 'heading', label: 'Connections', section: 'profile' },
    ...(options.includePlugins ? [{ id: `${WORKSPACE_PROFILE_TAB_PREFIX}integrations/gmail-addon`, label: 'Gmail add-on', icon: Mail, section: 'profile' as const, sfSymbol: 'envelope' }] : []),
    { id: '__h-preferences', kind: 'heading', label: 'Preferences', section: 'profile' },
    ...SETTINGS_TABS.filter(tab=>tab.id !== 'availability').map(tab=>{const appearance=PREFERENCE_SECTIONS.find(section=>section.id===tab.sections[0])!;return {id:`preferences/${tab.id}`,label:tab.label,icon:appearance.icon,section:'profile' as const,sfSymbol:appearance.sfSymbol};}),
  ];

  if (options.includePlugins) personalItems.push({ id: `${WORKSPACE_PROFILE_TAB_PREFIX}integrations`, label: 'Workspace plugins', icon: Plug, section: 'profile', sfSymbol: 'powerplug' });

  if (!options.includeWorkspaceSettings) return options.includeAgents
    ? [...personalItems, { id: `${WORKSPACE_PROFILE_TAB_PREFIX}bots`, label: 'AI Agents', icon: Bot, section: 'profile', sfSymbol: 'sparkles' }]
    : personalItems;

  // Self-service settings are deliberately expressed in customer language.
  // The directory's broader Organisation / Accounts / Quantis / Workspace
  // taxonomy describes how internal staff administer a customer; it made a
  // workspace owner navigate around Quantis' org chart. Keep the same secure
  // destinations, but group them by what the owner is trying to manage.
  const workspaceItems: SidebarNavItem[] = [
    { id: '__h-workspace', kind: 'heading', label: 'Workspace', section: 'profile' },
    { id: `${WORKSPACE_PROFILE_TAB_PREFIX}overview`,      label: 'Organisation',  icon: Building2,   section: 'profile', sfSymbol: 'building.2.fill' },
    { id: `${WORKSPACE_PROFILE_TAB_PREFIX}users`,         label: 'Users',         icon: Users,       section: 'profile', sfSymbol: 'person.3.fill' },
    { id: `${WORKSPACE_PROFILE_TAB_PREFIX}bots`,          label: 'AI Agents',     icon: Bot,         section: 'profile', sfSymbol: 'sparkles' },
    { id: `${WORKSPACE_PROFILE_TAB_PREFIX}documents`,     label: 'Workspace documents', icon: FolderOpen,  section: 'profile', sfSymbol: 'folder.fill' },
    { id: '__h-billing', kind: 'heading', label: 'Billing', section: 'profile' },
    { id: `${WORKSPACE_PROFILE_TAB_PREFIX}stripe-invoices`, label: 'Invoices', icon: Receipt,     section: 'profile', sfSymbol: 'doc.text.fill' },
  ];

  return [...personalItems, ...workspaceItems];
}

export function buildWorkspacesSidebarTabItems(_context: {
  isAdmin: boolean;
  pendingRequests?: number;
}): SidebarNavItem[] {
  return [
    { id: 'clients', label: 'Workspaces', icon: Users, section: 'clients', sfSymbol: 'person.3.fill' },
  ];
}

// Section nav for a single workspace, rendered inside the main sidebar when
// a workspace is selected in the directory split.
// Three concerns split across four sections:
//   1. ORGANISATION  — outward-facing letterhead (used on payment apps)
//   2. ACCOUNTS      — the workspace's own books (Xero / QuickBooks-style)
//   3. QUANTIS       — what we use to bill them
//   4. WORKSPACE     — operational sections (docs, resources, users, audit)
export function buildWorkspaceDetailSidebarTabItems(
  options: {
    includeAccounts?: boolean;
    includeDocuments?: boolean;
    includeIntegrations?: boolean;
    includeMembership?: boolean;
    includeSubscription?: boolean;
  } = {},
): SidebarNavItem[] {
  const items: SidebarNavItem[] = [
    { id: '__h-org',      kind: 'heading', label: 'Organisation',     section: 'clients' },
    { id: 'overview',      label: 'Organisation',  icon: Building2,   section: 'clients', sfSymbol: 'building.2.fill' },
    { id: 'trade-profile', label: 'Trade Profile', icon: Landmark,    section: 'clients', sfSymbol: 'building.columns.fill' },

    { id: '__h-accounts', kind: 'heading', label: 'Accounts',         section: 'clients' },
    { id: 'sales',           label: 'Sales',           icon: FileText, section: 'clients', sfSymbol: 'doc.plaintext.fill' },
    { id: 'purchase-orders', label: 'Purchase Orders', icon: Package,  section: 'clients', sfSymbol: 'shippingbox.fill' },

    { id: '__h-quantis',  kind: 'heading', label: 'Quantis',          section: 'clients' },
    { id: 'subscription',  label: 'Subscription',  icon: Receipt,     section: 'clients', sfSymbol: 'doc.text.fill' },
    { id: 'membership',    label: 'Membership',    icon: Star,        section: 'clients', sfSymbol: 'star.fill' },
    { id: 'stripe-invoices', label: 'Invoices',    icon: Receipt,     section: 'clients', sfSymbol: 'doc.text.fill' },
    { id: 'credit',        label: 'Credit report',        icon: ShieldCheck, section: 'clients', sfSymbol: 'checkmark.shield.fill' },

    { id: '__h-workspace',kind: 'heading', label: 'Workspace',        section: 'clients' },
    { id: 'integrations',  label: 'Plugins',  icon: Plug,    section: 'clients', sfSymbol: 'powerplug' },
    { id: 'documents',     label: 'Workspace documents', icon: FolderOpen,  section: 'clients', sfSymbol: 'folder.fill' },
    { id: 'resource-library', label: 'Resource Library', icon: LibraryBooks, section: 'clients', sfSymbol: 'books.vertical.fill' },
    { id: 'users',         label: 'Users',         icon: Users,       section: 'clients', sfSymbol: 'person.3.fill' },
    { id: 'activity',      label: 'Activity',      icon: BarChart3,   section: 'clients', sfSymbol: 'chart.bar.fill' },
  ];

  // Projects has a first-class destination in the main app sidebar, so the
  // workspace detail deliberately does not repeat a read-only project summary.
  return items.filter((item) => {
    if (
      options.includeAccounts === false &&
      (item.id === '__h-accounts' || item.id === 'sales' || item.id === 'purchase-orders')
    ) return false;
    if (options.includeDocuments === false && item.id === 'documents') return false;
    if (options.includeIntegrations === false && item.id === 'integrations') return false;
    if (options.includeMembership === false && item.id === 'membership') return false;
    if (options.includeSubscription === false && item.id === 'subscription') return false;
    return true;
  });
}

// Section nav for a single employer, rendered in place of the app nav while a
// profile is drilled into — the Workspaces treatment above, one level up.
// Each id must appear in EMPLOYER_DETAIL_SECTION_IDS (features/employers/
// constants.ts) and have a matching `activeSection ===` branch in
// EmployerProfilePanel — a row SWAPS the panel's page, it does not scroll to an
// anchor. This module imports nothing from features by design, so the pairing is
// by hand: add a section to the panel and it needs a row here, or it is unreachable.
//   1. PROFILE  — who they are and what we have traded with them
//   2. RECORDS  — the free-text and people attached to them
export function buildEmployerDetailSidebarTabItems(): SidebarNavItem[] {
  return [
    { id: '__h-profile', kind: 'heading', label: 'Profile',         section: 'crm' },
    { id: 'details',         label: 'Details',         icon: Building2,   section: 'crm', sfSymbol: 'building.2.fill' },
    { id: 'trading-history', label: 'Trading History', icon: History,     section: 'crm', sfSymbol: 'clock.arrow.circlepath' },
    { id: 'risk-profile',    label: 'Risk Profile',    icon: ShieldCheck, section: 'crm', sfSymbol: 'checkmark.shield.fill' },

    { id: '__h-records', kind: 'heading', label: 'Records',         section: 'crm' },
    { id: 'notes',           label: 'Notes',           icon: StickyNote,  section: 'crm', sfSymbol: 'note.text' },
    { id: 'contacts',        label: 'Contacts',        icon: Users,       section: 'crm', sfSymbol: 'person.3.fill' },
  ];
}

// Section nav for the employer ADD/EDIT FORM — one level deeper than the profile
// above, and the same hand-pairing rule: each id must appear in
// EMPLOYER_FORM_SECTION_IDS (features/employers/constants.ts) and have a
// matching `activeSection ===` branch in EmployerForm, which drops its own
// in-content nav column because these rows replace it.
// `visibleSectionIds` is a parameter, unlike every other builder here, because
// this list is conditional: Credit Check exists only once there is a company
// name or number to check. The form broadcasts the surviving ids on
// EMPLOYER_FORM_STATE_EVENT rather than the condition, so the rows can never
// offer a page the form would refuse to render.
// Flat, no headings: seven rows is short enough to read at a glance, and a
// heading whose only row is filtered out would render as a stray label.
export function buildEmployerFormSidebarTabItems(
  visibleSectionIds: readonly string[],
): SidebarNavItem[] {
  const rows: SidebarNavItem[] = [
    { id: 'company',   label: 'Company',      icon: Building2,   section: 'crm', sfSymbol: 'building.2.fill' },
    { id: 'address',   label: 'Address',      icon: MapPin,      section: 'crm', sfSymbol: 'mappin.and.ellipse' },
    { id: 'contact',   label: 'Contact',      icon: Phone,       section: 'crm', sfSymbol: 'phone.fill' },
    { id: 'invoicing', label: 'Invoicing',    icon: Receipt,     section: 'crm', sfSymbol: 'doc.text.fill' },
    { id: 'notes',     label: 'Notes',        icon: StickyNote,  section: 'crm', sfSymbol: 'note.text' },
    { id: 'people',    label: 'Key Contacts', icon: Users,       section: 'crm', sfSymbol: 'person.3.fill' },
    { id: 'credit',    label: 'Credit Check', icon: ShieldCheck, section: 'crm', sfSymbol: 'checkmark.shield.fill' },
  ];
  return rows.filter((row) => visibleSectionIds.includes(row.id));
}
